import { useState, useEffect } from 'react';
import type { Node, Edge } from 'reactflow';
import { MousePointer2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input, Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Textarea, Checkbox, Switch, Label } from '@/components/ui';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import PrimitiveSelector from './PrimitiveSelector';
import PrimitiveMappingEditor from './PrimitiveMappingEditor';
import type { FlowDefinition } from '@simcheck/shared-types';

interface PropertiesPanelProps {
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  onUpdateNode: (nodeId: string, data: any) => void;
  onClose: () => void;
}

export function PropertiesPanel({
  selectedNode,
  selectedEdge,
  onUpdateNode,
  onClose
}: PropertiesPanelProps) {
  const [localData, setLocalData] = useState<any>({});

  // Update local state when selection changes
  useEffect(() => {
    if (selectedNode) {
      setLocalData(selectedNode.data || {});
    } else {
      setLocalData({});
    }
  }, [selectedNode?.id]);

  const handleChange = (field: string, value: any) => {
    setLocalData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    if (selectedNode) {
      onUpdateNode(selectedNode.id, localData);
    }
  };

  const renderNodeProperties = () => {
    if (!selectedNode) return null;

    switch (selectedNode.type) {
      case 'atCommand':
        // Initialize atCommand object if it doesn't exist
        if (!localData.atCommand) {
          localData.atCommand = {};
        }
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="command">AT Command *</Label>
              <Input
                id="command"
                value={localData.atCommand.command || ''}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    atCommand: { ...prev.atCommand, command: e.target.value }
                  }));
                }}
                placeholder="e.g., AT+CSQ, AT+COPS?"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Must start with "AT" (e.g., AT+CSQ)
              </p>
            </div>

            <div>
              <Label htmlFor="expectedResponse">
                Expected Response {!localData.atCommand.acceptAnyResponse && '*'}
              </Label>
              <Input
                id="expectedResponse"
                value={localData.atCommand.expectedResponse || ''}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    atCommand: { ...prev.atCommand, expectedResponse: e.target.value }
                  }));
                }}
                placeholder="e.g., OK, +CSQ:"
                disabled={localData.atCommand.acceptAnyResponse}
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                {localData.atCommand.acceptAnyResponse 
                  ? 'Response validation disabled - any response will be accepted' 
                  : 'Expected response pattern to validate'}
              </p>
            </div>

            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => {
              setLocalData((prev: any) => ({
                ...prev,
                atCommand: { 
                  ...prev.atCommand, 
                  acceptAnyResponse: !prev.atCommand.acceptAnyResponse,
                  // Clear expectedResponse if enabling acceptAnyResponse
                  ...((!prev.atCommand.acceptAnyResponse) && { expectedResponse: '' })
                }
              }));
            }}>
              <input
                type="checkbox"
                id="acceptAnyResponse"
                checked={localData.atCommand.acceptAnyResponse || false}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    atCommand: { 
                      ...prev.atCommand, 
                      acceptAnyResponse: e.target.checked,
                      // Clear expectedResponse if enabling acceptAnyResponse
                      ...(e.target.checked && { expectedResponse: '' })
                    }
                  }));
                }}
                className="w-4 h-4 text-accent-blue bg-muted border-border dark:border-border-subtle rounded focus:ring-accent-blue"
              />
              <Label htmlFor="acceptAnyResponse" className="cursor-pointer">
                Accept Any Response
              </Label>
            </div>

            <div>
              <Label htmlFor="storeAs">Store Result As Variable</Label>
              <Input
                id="storeAs"
                value={localData.atCommand.storeAs || ''}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    atCommand: { ...prev.atCommand, storeAs: e.target.value }
                  }));
                }}
                placeholder="e.g., signalQuality"
                className="font-mono"
              />
              {localData.atCommand.storeAs && (
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Reference as: ${'{'}${localData.atCommand.storeAs}{'}'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="timeout">Timeout (ms)</Label>
                <Input
                  id="timeout"
                  type="number"
                  value={localData.atCommand.timeout || 5000}
                  onChange={(e) => {
                    setLocalData((prev: any) => ({
                      ...prev,
                      atCommand: { ...prev.atCommand, timeout: parseInt(e.target.value) || 5000 }
                    }));
                  }}
                  min="100"
                  step="100"
                />
              </div>
              <div>
                <Label htmlFor="retries">Retries</Label>
                <Input
                  id="retries"
                  type="number"
                  value={localData.atCommand.retries !== undefined ? localData.atCommand.retries : 3}
                  onChange={(e) => {
                    setLocalData((prev: any) => ({
                      ...prev,
                      atCommand: { ...prev.atCommand, retries: parseInt(e.target.value) || 0 }
                    }));
                  }}
                  min="0"
                  max="5"
                />
              </div>
            </div>
            
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={localData.atCommand.parseResponse !== false}
                  onChange={(e) => {
                    setLocalData((prev: any) => ({
                      ...prev,
                      atCommand: { ...prev.atCommand, parseResponse: e.target.checked }
                    }));
                  }}
                  className="rounded border-border"
                />
                <span className="text-sm text-foreground">
                  Parse Response
                </span>
              </label>
            </div>
          </div>
        );

      case 'condition':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="condition">Condition Expression</Label>
              <textarea
                id="condition"
                value={localData.condition || ''}
                onChange={(e) => handleChange('condition', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 rounded-lg font-mono text-sm",
                  "bg-background",
                  "border border-border",
                  "text-foreground",
                  "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue",
                  "transition-colors"
                )}
                rows={3}
                placeholder="e.g., $response.includes('OK')"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                JavaScript expression. Use ${'{'}varName{'}'} for variables.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="trueLabel">True Label</Label>
                <Input
                  id="trueLabel"
                  value={localData.trueLabel || 'True'}
                  onChange={(e) => handleChange('trueLabel', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="falseLabel">False Label</Label>
                <Input
                  id="falseLabel"
                  value={localData.falseLabel || 'False'}
                  onChange={(e) => handleChange('falseLabel', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 'delay':
        // Initialize delay object if it doesn't exist
        if (!localData.delay) {
          localData.delay = { duration: 1, unit: 's' };
        }
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="duration">Duration *</Label>
              <Input
                id="duration"
                type="number"
                value={localData.delay.duration || 1}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    delay: { ...prev.delay, duration: parseInt(e.target.value) || 1 }
                  }));
                }}
                min="0"
                step="1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">Unit *</Label>
              <Select
                value={localData.delay.unit || 's'}
                onValueChange={(value) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    delay: { ...prev.delay, unit: value }
                  }));
                }}
              >
                <SelectTrigger id="unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ms">Milliseconds (ms)</SelectItem>
                  <SelectItem value="s">Seconds (s)</SelectItem>
                  <SelectItem value="m">Minutes (m)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Time unit: ms, s, or m
              </p>
            </div>
          </div>
        );

      case 'variable':
        // Canonical shape is nested data.variable.* (the executor reads it) —
        // migrate legacy flat variableName/operation/value here so Save
        // rewrites the node canonically (#203)
        if (!localData.variable) {
          localData.variable = {
            name: localData.variableName || '',
            operation: localData.operation || 'set',
            value: localData.value ?? '',
            type: 'string',
          };
          delete localData.variableName;
          delete localData.operation;
          delete localData.value;
        }
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="variableName">Variable Name</Label>
              <Input
                id="variableName"
                value={localData.variable.name || ''}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    variable: { ...prev.variable, name: e.target.value }
                  }));
                }}
                placeholder="e.g., counter"
                className="font-mono"
              />
            </div>

            <div>
              <Label htmlFor="operation">Operation</Label>
              <select
                id="operation"
                value={localData.variable.operation || 'set'}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    variable: { ...prev.variable, operation: e.target.value }
                  }));
                }}
                className={cn(
                  "w-full px-3 py-2 rounded-lg",
                  "bg-background",
                  "border border-border",
                  "text-foreground",
                  "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue",
                  "transition-colors"
                )}
              >
                <option value="set">Set</option>
                <option value="set_literal">Set (raw text, no ${} expansion)</option>
                <option value="get">Get</option>
                <option value="transform">Transform</option>
                <option value="random_choice">Random from list</option>
              </select>
            </div>

            {localData.variable.operation === 'set_literal' ? (
              <div>
                <Label htmlFor="value">Value (raw text)</Label>
                <textarea
                  id="value"
                  value={localData.variable.value ?? ''}
                  onChange={(e) => {
                    setLocalData((prev: any) => ({
                      ...prev,
                      variable: { ...prev.variable, value: e.target.value }
                    }));
                  }}
                  rows={8}
                  placeholder={"Raw text stored verbatim. ${var} placeholders are NOT expanded here — keep them for a downstream consumer (e.g. an agent prompt containing ${pin})."}
                  className={cn(
                    "w-full px-3 py-2 rounded-lg font-mono text-sm",
                    "bg-background border border-border text-foreground",
                    "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue transition-colors"
                  )}
                />
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Stored verbatim. <span className="font-mono">${'{var}'}</span> references are kept as-is (not expanded) — use for prompts/templates whose secrets must resolve downstream.
                </p>
              </div>
            ) : localData.variable.operation === 'random_choice' ? (
              <div>
                <Label htmlFor="value">Options (one per line)</Label>
                <textarea
                  id="value"
                  value={
                    Array.isArray(localData.variable.value)
                      ? localData.variable.value.join('\n')
                      : (localData.variable.value ?? '')
                  }
                  onChange={(e) => {
                    // Store as an array — the executor picks one at random per run.
                    const list = e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    setLocalData((prev: any) => ({
                      ...prev,
                      variable: { ...prev.variable, value: list, type: 'array' }
                    }));
                  }}
                  rows={6}
                  placeholder={"+972547676370\n+972547676371\n+972547676373"}
                  className={cn(
                    "w-full px-3 py-2 rounded-lg font-mono text-sm",
                    "bg-background border border-border text-foreground",
                    "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue transition-colors"
                  )}
                />
                <p className="text-xs text-muted-foreground/70 mt-1">
                  One value per line. A single random entry is chosen each run. Items support <span className="font-mono">${'{var}'}</span> substitution.
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="value">Value</Label>
                <Input
                  id="value"
                  value={localData.variable.value ?? ''}
                  onChange={(e) => {
                    setLocalData((prev: any) => ({
                      ...prev,
                      variable: { ...prev.variable, value: e.target.value }
                    }));
                  }}
                  placeholder="e.g., 0"
                />
              </div>
            )}
          </div>
        );

      case 'loop':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="iterations">Iterations</Label>
              <Input
                id="iterations"
                type="number"
                value={localData.iterations || 1}
                onChange={(e) => handleChange('iterations', parseInt(e.target.value))}
                min="1"
              />
            </div>

            <div>
              <Label htmlFor="variable">Loop Variable</Label>
              <Input
                id="variable"
                value={localData.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="e.g., i"
                className="font-mono"
              />
            </div>
          </div>
        );

      case 'assertion':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="assertion">Assertion</Label>
              <textarea
                id="assertion"
                value={localData.assertion || ''}
                onChange={(e) => handleChange('assertion', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 rounded-lg font-mono text-sm",
                  "bg-background",
                  "border border-border",
                  "text-foreground",
                  "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue",
                  "transition-colors"
                )}
                rows={2}
                placeholder="e.g., $counter > 0"
              />
            </div>

            <div>
              <Label htmlFor="errorMessage">Error Message</Label>
              <Input
                id="errorMessage"
                value={localData.errorMessage || ''}
                onChange={(e) => handleChange('errorMessage', e.target.value)}
                placeholder="e.g., Counter must be positive"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="continueOnFailure"
                checked={localData.continueOnFailure || false}
                onChange={(e) => handleChange('continueOnFailure', e.target.checked)}
                className="w-4 h-4 rounded border-border text-accent-blue focus:ring-accent-blue"
              />
              <Label htmlFor="continueOnFailure" className="mb-0">Continue on failure</Label>
            </div>
          </div>
        );

      case 'log':
        // Initialize log object if it doesn't exist
        if (!localData.log) {
          localData.log = {};
        }
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="logMessage">Log Message *</Label>
              <textarea
                id="logMessage"
                value={localData.log.message || ''}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    log: { ...prev.log, message: e.target.value }
                  }));
                }}
                className={cn(
                  "w-full px-3 py-2 rounded-lg font-mono text-sm",
                  "bg-background",
                  "border border-border",
                  "text-foreground",
                  "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue",
                  "transition-colors"
                )}
                rows={3}
                placeholder="e.g., Device info: ${deviceInfo}"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Use ${'{'}varName{'}'} to include variable values
              </p>
            </div>

            <div>
              <Label htmlFor="logLevel">Log Level</Label>
              <select
                id="logLevel"
                value={localData.log.level || 'info'}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    log: { ...prev.log, level: e.target.value }
                  }));
                }}
                className={cn(
                  "w-full px-3 py-2 rounded-lg text-sm",
                  "bg-background",
                  "border border-border",
                  "text-foreground",
                  "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue"
                )}
              >
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="outputVariables"
                checked={localData.log.outputVariables || false}
                onChange={(e) => {
                  setLocalData((prev: any) => ({
                    ...prev,
                    log: { ...prev.log, outputVariables: e.target.checked }
                  }));
                }}
                className="w-4 h-4 rounded border-border text-accent-blue focus:ring-accent-blue"
              />
              <Label htmlFor="outputVariables" className="mb-0">Also output all variables</Label>
            </div>
            <p className="text-xs text-muted-foreground/70">
              Useful for debugging - outputs all variable names and values
            </p>
          </div>
        );

      case 'end':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="endSummary">Summary</Label>
              <textarea
                id="endSummary"
                value={localData.summary || ''}
                onChange={(e) => handleChange('summary', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 rounded-lg font-mono text-sm",
                  "bg-background",
                  "border border-border",
                  "text-foreground",
                  "focus:border-accent-blue focus:ring-1 focus:ring-accent-blue",
                  "transition-colors"
                )}
                rows={4}
                placeholder={'e.g., Ping ${pingResult.rtt_avg_ms}ms avg, ${pingResult.packet_loss_pct}% loss'}
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Shown as the test run summary. Use ${'{'}varName{'}'} or ${'{'}obj.field{'}'} to reference flow variables. Leave empty to fall back to the default summary.
              </p>
            </div>
          </div>
        );

      case 'primitiveCall':
        return <PrimitiveCallProperties data={localData} onChange={setLocalData} />;

      case 'transform':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="transformType">Transform Type</Label>
              <select
                id="transformType"
                value={(localData.transform as any)?.transformType || 'extract'}
                onChange={(e) => setLocalData((prev: any) => ({
                  ...prev,
                  transform: { ...(prev.transform as any), transformType: e.target.value }
                }))}
                className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="extract">Extract (JSON path)</option>
                <option value="parse_json">Parse JSON</option>
                <option value="stringify">Stringify</option>
                <option value="map">Map</option>
                <option value="filter">Filter</option>
                <option value="custom">Custom Expression</option>
              </select>
            </div>
            <div>
              <Label htmlFor="inputExpression">Input Expression</Label>
              <Input
                id="inputExpression"
                value={(localData.transform as any)?.inputExpression || ''}
                onChange={(e) => setLocalData((prev: any) => ({
                  ...prev,
                  transform: { ...(prev.transform as any), inputExpression: e.target.value }
                }))}
                placeholder="{{ variables.rawData }}"
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Expression to transform (use {'{{ }}'} syntax)
              </p>
            </div>
            {(localData.transform as any)?.transformType === 'extract' && (
              <div>
                <Label htmlFor="extractPath">Extract Path</Label>
                <Input
                  id="extractPath"
                  value={(localData.transform as any)?.extractPath || ''}
                  onChange={(e) => setLocalData((prev: any) => ({
                    ...prev,
                    transform: { ...(prev.transform as any), extractPath: e.target.value }
                  }))}
                  placeholder="response.data.signal"
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground/70 mt-1">
                  JSON path to extract from input
                </p>
              </div>
            )}
            {(localData.transform as any)?.transformType === 'custom' && (
              <div>
                <Label htmlFor="customTransform">Custom Expression</Label>
                <textarea
                  id="customTransform"
                  value={(localData.transform as any)?.customTransform || ''}
                  onChange={(e) => setLocalData((prev: any) => ({
                    ...prev,
                    transform: { ...(prev.transform as any), customTransform: e.target.value }
                  }))}
                  placeholder="input.split(',').map(x => x.trim())"
                  rows={3}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
                <p className="text-xs text-muted-foreground/70 mt-1">
                  JavaScript expression (input variable available)
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="outputVariable">Output Variable</Label>
              <Input
                id="outputVariable"
                value={(localData.transform as any)?.outputVariable || ''}
                onChange={(e) => setLocalData((prev: any) => ({
                  ...prev,
                  transform: { ...(prev.transform as any), outputVariable: e.target.value }
                }))}
                placeholder="signalStrength"
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                Variable name to store the result
              </p>
            </div>
          </div>
        );

      case 'dataTest': {
        const dt = localData.dataTest || {};
        const setDt = (patch: Record<string, any>) =>
          setLocalData((prev: any) => ({ ...prev, dataTest: { ...prev.dataTest, ...patch } }));
        const tt = dt.testType as string | undefined;
        const isPing = tt === 'ping';
        const isHop = tt === 'traceroute' || tt === 'mtr';
        const isHttp = tt === 'http' || tt === 'http_post' || tt === 'download';
        const isDns = tt === 'dns';
        const isTransfer = tt === 'ftp' || tt === 'sftp';
        const isSpeed = tt === 'speedtest_parallel';
        const isBringup = tt === 'bringup' || tt === 'multi_bringup';
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="dt-type">Test Type *</Label>
              <Select value={tt || ''} onValueChange={(v) => setDt({ testType: v })}>
                <SelectTrigger id="dt-type"><SelectValue placeholder="Select a test type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ping">Ping</SelectItem>
                  <SelectItem value="http">HTTP fetch</SelectItem>
                  <SelectItem value="http_post">HTTP POST</SelectItem>
                  <SelectItem value="download">Download</SelectItem>
                  <SelectItem value="dns">DNS query</SelectItem>
                  <SelectItem value="traceroute">Traceroute</SelectItem>
                  <SelectItem value="mtr">MTR</SelectItem>
                  <SelectItem value="ftp">FTP</SelectItem>
                  <SelectItem value="sftp">SFTP</SelectItem>
                  <SelectItem value="email">Email (SMTP/IMAP)</SelectItem>
                  <SelectItem value="speedtest_parallel">Speedtest (parallel)</SelectItem>
                  <SelectItem value="bringup">Bring-up</SelectItem>
                  <SelectItem value="multi_bringup">Multi-APN bring-up</SelectItem>
                  <SelectItem value="teardown">Teardown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(isPing || isHop) && (
              <div>
                <Label htmlFor="dt-target">Target host / IP</Label>
                <Input id="dt-target" value={dt.target || ''} onChange={(e) => setDt({ target: e.target.value })} placeholder="e.g., 8.8.8.8" className="font-mono" />
              </div>
            )}
            {isPing && (
              <div>
                <Label htmlFor="dt-count">Ping count</Label>
                <Input id="dt-count" type="number" min="1" value={dt.count ?? ''} onChange={(e) => setDt({ count: parseInt(e.target.value) || undefined })} />
              </div>
            )}
            {isHop && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="dt-maxhops">Max hops</Label>
                  <Input id="dt-maxhops" type="number" min="1" value={dt.maxHops ?? ''} onChange={(e) => setDt({ maxHops: parseInt(e.target.value) || undefined })} />
                </div>
                <div>
                  <Label htmlFor="dt-cycles">Cycles</Label>
                  <Input id="dt-cycles" type="number" min="1" value={dt.cycles ?? ''} onChange={(e) => setDt({ cycles: parseInt(e.target.value) || undefined })} />
                </div>
              </div>
            )}
            {(isHttp || isTransfer) && (
              <div>
                <Label htmlFor="dt-url">URL</Label>
                <Input id="dt-url" value={dt.url || ''} onChange={(e) => setDt({ url: e.target.value })} placeholder="https://example.com/file" className="font-mono" />
              </div>
            )}
            {isHttp && (
              <div>
                <Label htmlFor="dt-expect">Expected content substring</Label>
                <Input id="dt-expect" value={dt.expectedContentSubstring || ''} onChange={(e) => setDt({ expectedContentSubstring: e.target.value })} placeholder="optional" />
              </div>
            )}
            {isDns && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="dt-hostname">Hostname</Label>
                  <Input id="dt-hostname" value={dt.hostname || ''} onChange={(e) => setDt({ hostname: e.target.value })} placeholder="e.g., example.com" className="font-mono" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="dt-record">Record</Label>
                    <Select value={dt.record || 'A'} onValueChange={(v) => setDt({ record: v })}>
                      <SelectTrigger id="dt-record"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="AAAA">AAAA</SelectItem>
                        <SelectItem value="MX">MX</SelectItem>
                        <SelectItem value="TXT">TXT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="dt-resolver">Resolver</Label>
                    <Input id="dt-resolver" value={dt.resolver || ''} onChange={(e) => setDt({ resolver: e.target.value })} placeholder="optional" className="font-mono" />
                  </div>
                </div>
              </div>
            )}
            {isTransfer && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="dt-host">Host</Label>
                    <Input id="dt-host" value={dt.host || ''} onChange={(e) => setDt({ host: e.target.value })} className="font-mono" />
                  </div>
                  <div>
                    <Label htmlFor="dt-port">Port</Label>
                    <Input id="dt-port" type="number" value={dt.port ?? ''} onChange={(e) => setDt({ port: parseInt(e.target.value) || undefined })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="dt-user">Username</Label>
                    <Input id="dt-user" value={dt.username || ''} onChange={(e) => setDt({ username: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="dt-pass">Password</Label>
                    <Input id="dt-pass" type="password" value={dt.password || ''} onChange={(e) => setDt({ password: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="dt-remote">Remote path</Label>
                  <Input id="dt-remote" value={dt.remotePath || ''} onChange={(e) => setDt({ remotePath: e.target.value })} placeholder="/upload/test.bin" className="font-mono" />
                </div>
              </div>
            )}
            {isSpeed && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="dt-streams">Streams</Label>
                  <Input id="dt-streams" type="number" min="1" value={dt.streams ?? ''} onChange={(e) => setDt({ streams: parseInt(e.target.value) || undefined })} />
                </div>
                <div>
                  <Label htmlFor="dt-perstream">Bytes / stream</Label>
                  <Input id="dt-perstream" type="number" min="1" value={dt.perStreamBytes ?? ''} onChange={(e) => setDt({ perStreamBytes: parseInt(e.target.value) || undefined })} />
                </div>
              </div>
            )}
            {isBringup && (
              <div>
                <Label htmlFor="dt-apns">APNs (comma-separated)</Label>
                <Input id="dt-apns" value={(dt.apns || []).join(', ')} onChange={(e) => setDt({ apns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="internet, ims" className="font-mono" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dt-apn">APN (override)</Label>
                <Input id="dt-apn" value={dt.apn || ''} onChange={(e) => setDt({ apn: e.target.value })} placeholder="auto" className="font-mono" />
              </div>
              <div>
                <Label htmlFor="dt-timeout">Timeout (ms)</Label>
                <Input id="dt-timeout" type="number" min="1000" step="1000" value={dt.timeout ?? 60000} onChange={(e) => setDt({ timeout: parseInt(e.target.value) || 60000 })} />
              </div>
            </div>
            <div>
              <Label htmlFor="dt-store">Store Result As Variable</Label>
              <Input id="dt-store" value={dt.storeAs || ''} onChange={(e) => setDt({ storeAs: e.target.value })} placeholder="e.g., pingResult" className="font-mono" />
            </div>
          </div>
        );
      }

      case 'voiceTest': {
        const vt = localData.voiceTest || {};
        const setVt = (patch: Record<string, any>) =>
          setLocalData((prev: any) => ({ ...prev, voiceTest: { ...prev.voiceTest, ...patch } }));
        const kind = vt.kind as string | undefined;
        const dials = kind === 'dial' || kind === 'call_basic' || kind === 'call_quality';
        const isDtmf = kind === 'send_dtmf' || kind === 'detect_dtmf' || kind === 'dtmf_roundtrip';
        const isQuality = kind === 'call_quality';
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="vt-kind">Kind *</Label>
              <Select value={kind || ''} onValueChange={(v) => setVt({ kind: v })}>
                <SelectTrigger id="vt-kind"><SelectValue placeholder="Select a voice test kind" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ims_status">IMS status</SelectItem>
                  <SelectItem value="tech_probe">Tech probe</SelectItem>
                  <SelectItem value="dial">Dial</SelectItem>
                  <SelectItem value="hangup">Hang up</SelectItem>
                  <SelectItem value="call_basic">Call (basic)</SelectItem>
                  <SelectItem value="call_quality">Call quality (MOS)</SelectItem>
                  <SelectItem value="send_dtmf">Send DTMF</SelectItem>
                  <SelectItem value="detect_dtmf">Detect DTMF</SelectItem>
                  <SelectItem value="dtmf_roundtrip">DTMF round-trip</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dials && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="vt-dial">Dial number</Label>
                  <Input id="vt-dial" value={vt.dialNumber || ''} onChange={(e) => setVt({ dialNumber: e.target.value })} placeholder="+1..." className="font-mono" />
                </div>
                <div>
                  <Label htmlFor="vt-dur">Duration (s)</Label>
                  <Input id="vt-dur" type="number" min="1" value={vt.durationSec ?? ''} onChange={(e) => setVt({ durationSec: parseInt(e.target.value) || undefined })} />
                </div>
              </div>
            )}
            {isQuality && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="vt-mode">Mode</Label>
                  <Select value={vt.mode || 'auto'} onValueChange={(v) => setVt({ mode: v })}>
                    <SelectTrigger id="vt-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="paired">Paired</SelectItem>
                      <SelectItem value="echo">Echo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="vt-codec">Codec</Label>
                  <Select value={vt.codecMode || 'auto'} onValueChange={(v) => setVt({ codecMode: v })}>
                    <SelectTrigger id="vt-codec"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="nb">Narrowband</SelectItem>
                      <SelectItem value="wb">Wideband</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {isDtmf && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="vt-digits">DTMF digits</Label>
                  <Input id="vt-digits" value={vt.dtmfDigits || ''} onChange={(e) => setVt({ dtmfDigits: e.target.value })} placeholder="123#" className="font-mono" />
                </div>
                <div>
                  <Label htmlFor="vt-window">Detect window (ms)</Label>
                  <Input id="vt-window" type="number" min="100" step="100" value={vt.dtmfDetectWindowMs ?? ''} onChange={(e) => setVt({ dtmfDetectWindowMs: parseInt(e.target.value) || undefined })} />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="vt-bearer">Expected bearer (assertion)</Label>
              <Select value={vt.expectedBearer || 'none'} onValueChange={(v) => setVt({ expectedBearer: v === 'none' ? undefined : v })}>
                <SelectTrigger id="vt-bearer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assertion</SelectItem>
                  <SelectItem value="VoLTE">VoLTE</SelectItem>
                  <SelectItem value="VoNR">VoNR</SelectItem>
                  <SelectItem value="CS-CSFB">CS-CSFB</SelectItem>
                  <SelectItem value="CS-2G3G">CS-2G3G</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="vt-timeout">Timeout (ms)</Label>
                <Input id="vt-timeout" type="number" min="1000" step="1000" value={vt.timeout ?? ''} onChange={(e) => setVt({ timeout: parseInt(e.target.value) || undefined })} placeholder="default" />
              </div>
              <div>
                <Label htmlFor="vt-store">Store As</Label>
                <Input id="vt-store" value={vt.storeAs || ''} onChange={(e) => setVt({ storeAs: e.target.value })} placeholder="callResult" className="font-mono" />
              </div>
            </div>
          </div>
        );
      }

      case 'voiceAgentTest': {
        const va = localData.voiceAgentTest || {};
        const cfg = va.agentConfig || {};
        const setVa = (patch: Record<string, any>) =>
          setLocalData((prev: any) => ({ ...prev, voiceAgentTest: { ...prev.voiceAgentTest, ...patch } }));
        const setCfg = (patch: Record<string, any>) =>
          setLocalData((prev: any) => ({
            ...prev,
            voiceAgentTest: {
              ...prev.voiceAgentTest,
              agentConfig: { ...(prev.voiceAgentTest?.agentConfig || {}), ...patch },
            },
          }));
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="va-phone">Phone number *</Label>
              <Input
                id="va-phone"
                value={va.phoneNumber || ''}
                onChange={(e) => setVa({ phoneNumber: e.target.value })}
                placeholder="+14155284999 or ${phoneNumber}"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Number the agent dials (E.164, or a flow input like <span className="font-mono">${'{phoneNumber}'}</span>).
              </p>
            </div>

            <div>
              <Label htmlFor="va-goal">Agent instructions *</Label>
              <Textarea
                id="va-goal"
                value={cfg.goal || ''}
                onChange={(e) => setCfg({ goal: e.target.value })}
                placeholder={'Natural-language playbook. e.g. "Press 1 for English. When asked for the PIN enter ${pin}. When asked for a destination dial ${destination} then #. Finish when you hear ringing."'}
                rows={6}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Reference named inputs as <span className="font-mono">${'{pin}'}</span> / <span className="font-mono">${'{destination}'}</span> — substituted agent-side so secrets stay out of logs.
              </p>
            </div>

            <div>
              <Label htmlFor="va-extra">Additional instructions</Label>
              <Textarea
                id="va-extra"
                value={cfg.extraInstructions || ''}
                onChange={(e) => setCfg({ extraInstructions: e.target.value })}
                placeholder={'${extraInstructions}'}
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Added <em>on top of</em> the instructions above, not instead of them. Bind it to a flow input
                (<span className="font-mono">${'{extraInstructions}'}</span>) to let each run add its own notes —
                e.g. a card that asks for a second code. Left empty, nothing is sent.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="va-speak" className="cursor-pointer">Allow speech (TTS)</Label>
                <p className="text-xs text-muted-foreground">Off = DTMF-only (safest for IVR / calling cards).</p>
              </div>
              <Switch
                id="va-speak"
                checked={!!cfg.allowSpeak}
                onCheckedChange={(v) => setCfg({ allowSpeak: v })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="va-maxdur">Max duration (s)</Label>
                <Input
                  id="va-maxdur"
                  type="number"
                  min="10"
                  value={va.maxDurationSec ?? ''}
                  onChange={(e) => setVa({ maxDurationSec: parseInt(e.target.value) || undefined })}
                  placeholder="150"
                />
              </div>
              <div>
                <Label htmlFor="va-turns">Max turns</Label>
                <Input
                  id="va-turns"
                  type="number"
                  min="1"
                  value={cfg.maxTurns ?? ''}
                  onChange={(e) => setCfg({ maxTurns: parseInt(e.target.value) || undefined })}
                  placeholder="16"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="va-dtmf">Max DTMF digits</Label>
                <Input
                  id="va-dtmf"
                  type="number"
                  min="1"
                  value={cfg.maxDtmfDigits ?? ''}
                  onChange={(e) => setCfg({ maxDtmfDigits: parseInt(e.target.value) || undefined })}
                  placeholder="64"
                />
              </div>
              <div>
                <Label htmlFor="va-enc">Encoding</Label>
                <Select value={va.encoding || 's16le'} onValueChange={(v) => setVa({ encoding: v })}>
                  <SelectTrigger id="va-enc"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="s16le">s16le (16-bit PCM)</SelectItem>
                    <SelectItem value="mulaw">mulaw</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="va-store">Store As</Label>
              <Input
                id="va-store"
                value={va.storeAs || ''}
                onChange={(e) => setVa({ storeAs: e.target.value })}
                placeholder="agentResult"
                className="font-mono"
              />
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="text-sm text-muted-foreground">
            No properties available for this node type.
          </div>
        );
    }
  };

  // Empty state when nothing is selected — the rail still occupies space so
  // the user can drop in and out without the layout reflowing.
  if (!selectedNode && !selectedEdge) {
    return (
      <div className="h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-card border-b border-border dark:border-border-subtle px-3 py-2 flex items-center">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Inspector</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2 text-muted-foreground">
          <MousePointer2 className="w-6 h-6 opacity-60" />
          <p className="text-[13px]">Select a node or edge to edit its properties.</p>
        </div>
      </div>
    );
  }

  const nodeTypeLabel = selectedNode?.type?.replace(/([A-Z])/g, ' $1').trim();

  return (
    <div className="h-full flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border dark:border-border-subtle px-3 py-2 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
              {selectedNode ? nodeTypeLabel : 'Edge'}
            </div>
            {selectedNode && (
              <div className="text-[11px] font-mono text-muted-foreground/70 truncate">
                {selectedNode.id}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground/70 hover:text-foreground transition-colors flex-shrink-0"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {selectedNode && (
          <Accordion type="multiple" defaultValue={['properties']} className="px-3">
            <AccordionItem value="properties" className="border-b-0">
              <AccordionTrigger className="py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:no-underline">
                Properties
              </AccordionTrigger>
              <AccordionContent className="pb-3">{renderNodeProperties()}</AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {selectedEdge && (
          <div className="p-3 text-[13px] text-muted-foreground">
            Connection from{' '}
            <span className="font-mono text-foreground">{selectedEdge.source}</span> to{' '}
            <span className="font-mono text-foreground">{selectedEdge.target}</span>
          </div>
        )}
      </div>

      {/* Sticky footer */}
      {selectedNode && (
        <div className="sticky bottom-0 bg-card border-t border-border dark:border-border-subtle px-3 py-2 flex-shrink-0">
          <Button variant="default" size="sm" className="w-full" onClick={handleSave}>
            Apply Changes
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * PrimitiveCallProperties Component
 * 
 * Properties panel for Primitive Call nodes
 */
interface PrimitiveCallPropertiesProps {
  data: any;
  onChange: (data: any) => void;
}

function PrimitiveCallProperties({ data, onChange }: PrimitiveCallPropertiesProps) {
  const [showPrimitiveSelector, setShowPrimitiveSelector] = useState(false);
  const [selectedPrimitive, setSelectedPrimitive] = useState<FlowDefinition | null>(
    data.primitive || null
  );
  const [isLoadingPrimitive, setIsLoadingPrimitive] = useState(false);

  // BUG-014 FIX: Fetch primitive if only ID is available
  useEffect(() => {
    const primitiveId = data.primitiveCall?.primitiveId || data.primitiveId;

    // If we already have the full primitive object or no ID, skip fetch
    if (data.primitive || !primitiveId || isLoadingPrimitive) {
      return;
    }

    const fetchPrimitive = async () => {
      setIsLoadingPrimitive(true);
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/config/firebase');

        // Try primitives collection first (global primitives)
        const primitiveRef = doc(db, 'primitives', primitiveId);
        const primitiveSnap = await getDoc(primitiveRef);

        if (primitiveSnap.exists()) {
          const primitive = { id: primitiveSnap.id, ...primitiveSnap.data() } as FlowDefinition;
          setSelectedPrimitive(primitive);
        } else {
          // Try flows collection (org-scoped scenarios)
          const flowRef = doc(db, 'flows', primitiveId);
          const flowSnap = await getDoc(flowRef);

          if (flowSnap.exists()) {
            const primitive = { id: flowSnap.id, ...flowSnap.data() } as FlowDefinition;
            setSelectedPrimitive(primitive);
          }
        }
      } catch (error) {
        console.error('Failed to fetch primitive:', error);
      } finally {
        setIsLoadingPrimitive(false);
      }
    };

    fetchPrimitive();
  }, [data.primitive, data.primitiveCall?.primitiveId, data.primitiveId]);

  const handleSelectPrimitive = (primitive: FlowDefinition) => {
    setSelectedPrimitive(primitive);
    onChange({
      ...data,
      // Proper nested structure for validation
      primitiveCall: {
        primitiveId: primitive.id,
        inputMapping: data.primitiveCall?.inputMapping || {},
        outputMapping: data.primitiveCall?.outputMapping || {},
      },
      // Display helpers at top level (for UI convenience)
      primitive,
      primitive_name: primitive.name,
      primitive_type: primitive.category || 'General',
      description: primitive.description,
    });
  };

  const handleInputMappingChange = (mapping: Record<string, string>) => {
    onChange({
      ...data,
      primitiveCall: {
        ...data.primitiveCall,
        inputMapping: mapping,
      },
    });
  };

  const handleOutputMappingChange = (mapping: Record<string, string>) => {
    onChange({
      ...data,
      primitiveCall: {
        ...data.primitiveCall,
        outputMapping: mapping,
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Primitive Selection */}
      <div>
        <Label>Selected Primitive</Label>
        {selectedPrimitive ? (
          <div className="mt-2 bg-background border border-border rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-foreground mb-1">
                  {selectedPrimitive.name}
                </h4>
                {selectedPrimitive.description && typeof selectedPrimitive.description === 'string' && (
                  <p className="text-xs text-muted-foreground">
                    {selectedPrimitive.description}
                  </p>
                )}
                {selectedPrimitive.category && (
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-accent-blue/10 dark:bg-accent-blue/20 text-accent-blue rounded">
                    {selectedPrimitive.category}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowPrimitiveSelector(true)}
                className="text-xs text-accent-blue hover:underline"
              >
                Change
              </button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full mt-2"
            onClick={() => setShowPrimitiveSelector(true)}
          >
            Select Primitive
          </Button>
        )}
      </div>

      {/* Mapping Editor */}
      {selectedPrimitive && (
        <div className="pt-4 border-t border-border dark:border-border-subtle">
          <PrimitiveMappingEditor
            inputSchema={selectedPrimitive.inputSchema}
            outputSchema={selectedPrimitive.outputSchema}
            inputMapping={data.primitiveCall?.inputMapping || {}}
            outputMapping={data.primitiveCall?.outputMapping || {}}
            onInputMappingChange={handleInputMappingChange}
            onOutputMappingChange={handleOutputMappingChange}
          />
        </div>
      )}

      {/* Primitive Selector Modal */}
      <PrimitiveSelector
        isOpen={showPrimitiveSelector}
        onClose={() => setShowPrimitiveSelector(false)}
        onSelect={handleSelectPrimitive}
        selectedPrimitiveId={selectedPrimitive?.id}
      />
    </div>
  );
}

